// src/Pages/DisciplineGroups/DisciplineSubgroups.tsx
import React, { useState } from "react";
import Layout from "../../Containers/Layout";
import styles from "./DisciplineSubgroups.module.scss";
import { Button, Modal, Table, Tooltip, Typography } from "antd";
import { useQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PlusOutlined } from "@ant-design/icons";
import { useHistory, useParams } from "react-router-dom";
import { theme } from "../../theme";
import { NOTIFICATION_TYPES, openNotification } from "../Notifications/NotificationsUtils";

import {
  getDisciplineGroup,
  getSubjectsForGroup,
} from "../../Requests/discipline-group-requests";
import {
  addDisciplineSubgroup,
  deleteDisciplineSubgroup,
  getDisciplineSubgroups,
  updateDisciplineSubgroup,
} from "../../Requests/discipline-subgroup-requests";

import { SubjectMultiSelectModal } from "../DisciplineGroups/SubjectMultiSelectModal";
import { SubjectRequestType, SubjectsForGroupDTO } from "../../Api";

type SubjectOption = { value: string; label: string };

type SubjectsModalState = {
  open: boolean;
  title: string;
  groupId?: string;
  initialSelectedIds: string[];
  options: SubjectOption[];
  loading: boolean;
};

const DisciplineSubgroups: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useHistory()
  const { groupId } = useParams<{ groupId: string }>();
  const qc = useQueryClient();

  const [subjectsModal, setSubjectsModal] = useState<SubjectsModalState>({
    open: false,
    title: "",
    groupId: undefined,
    initialSelectedIds: [],
    options: [],
    loading: false,
  });
  const [editingSubgroup, setEditingSubgroup] = useState<any | null>(null);

  const [deleteDlg, setDeleteDlg] = useState<{ open: boolean; id?: string }>({
    open: false,
  });

  const invalidateQuery = () => {
    qc.invalidateQueries(["getDisciplineSubgroups", groupId]);
  };

  const openFetchError = (_error: any) => {
    openNotification(
      "Eroare!",
      "Datele nu au putut fi aduse din server!",
      NOTIFICATION_TYPES.ERROR
    );
  };

  const { data: group } = useQuery(['getDisciplineGroup', groupId],
      () => {
          return getDisciplineGroup(groupId);
      },
      {
          onError: openFetchError,
      }
  );

  const { data: page, isLoading } = useQuery(['getDisciplineSubgroups', groupId],
      () => {
          return getDisciplineSubgroups(groupId);
      },
      {
          onError: openFetchError,
      }
  );

  const openAddSubgroupModal = async () => {
    if (!groupId) return;
    setEditingSubgroup(null);
    setSubjectsModal((prev) => ({
      ...prev,
      open: true,
      title: "Adaugă subgrup",
      groupId,
      initialSelectedIds: [],
      loading: true,
      options: [],
    }));
    try {
      const disciplines: SubjectsForGroupDTO[] = await getSubjectsForGroup(
          group?.semester!,
          group?.studyYear!,
          group?.studyPlanId!,
          SubjectRequestType.ForSubgroupsAdd,
          undefined,             
          undefined,
      );     
      const options: SubjectOption[] = disciplines.map((s: any) => ({
          value: s.id,
          label: s.nameRO,
      }));

      setSubjectsModal(prev => ({ ...prev, options, loading: false }));
    } catch {
        setSubjectsModal(prev => ({ ...prev, loading: false }));
    }
  };

  const openEditSubgroupModal = async (subgroup: any) => {
    setEditingSubgroup(subgroup);
    const initialSelectedIds = subgroup?.subjects.map((s: any) =>
      String(s.id)
    );

    setSubjectsModal((prev) => ({
      ...prev,
      open: true,
      title: `Editează subgrup — ${subgroup?.name ?? ""}`,
      groupId,
      initialSelectedIds,
      loading: true,
      options: [],
    }));

    try {
      const disciplines: SubjectsForGroupDTO[] = await getSubjectsForGroup(
          group?.semester!,
          group?.studyYear!,
          group?.studyPlanId!,
          SubjectRequestType.ForSubgroupsEdit,
          subgroup.id,             
          undefined,
      );
      
      const options: SubjectOption[] = disciplines.map((s: any) => ({
          value: String(s.id),
          label: s.nameRO,
      }));

      setSubjectsModal(prev => ({ ...prev, options, loading: false }));
    } catch {
        setSubjectsModal(prev => ({ ...prev, loading: false }));
    }
  };

  const closeSubjectsModal = () => {
    setSubjectsModal((prev) => ({ ...prev, open: false }));
    setEditingSubgroup(null);
  };

  const handleSubmitSubgroup = async (subjectIds: string[], name?: string) => {
    if (!groupId) return;
    try {
      if (editingSubgroup?.id) {
        
        await updateDisciplineSubgroup(editingSubgroup.id, {
          name: name ?? editingSubgroup.name,
          groupId: groupId,
          subjectIds,
        });
        openNotification(
          "Succes",
          "Subgrupul a fost actualizat.",
          NOTIFICATION_TYPES.SUCCESS
        );
      } else {
        await addDisciplineSubgroup({
          name: name ?? "Subgrup",
          groupId: groupId,
          subjectIds,
        });
        openNotification(
          "Succes",
          "Subgrupul a fost creat.",
          NOTIFICATION_TYPES.SUCCESS
        );
      }
      invalidateQuery();
      closeSubjectsModal();
    } catch (err: any) {
      openNotification(
        "Eroare",
        err?.message ?? "Operația a eșuat.",
        NOTIFICATION_TYPES.ERROR
      );
    }
  };

  const handleDelete = (id: string) => setDeleteDlg({ open: true, id });
  const confirmDelete = async () => {
    if (!deleteDlg.id) return;
    try {
      await deleteDisciplineSubgroup(deleteDlg.id);
      openNotification(
        "Succes",
        "Ștergerea s-a realizat cu succes!",
        NOTIFICATION_TYPES.SUCCESS
      );
      invalidateQuery();
    } catch {
      openNotification(
        "Eroare",
        "Ștergerea nu s-a realizat cu succes!",
        NOTIFICATION_TYPES.ERROR
      );
    } finally {
      setDeleteDlg({ open: false });
    }
  };

  const columns = [
    {
      title: "Nume subgrup",
      dataIndex: "name",
      key: "name",
      render: (text: string) => text,
    },
    {
      title: "Acțiuni",
      key: "actions",
      width: 140,
      render: (_: any, record: any) => (
        <div>
          <Tooltip title="Editare">
            <FontAwesomeIcon
              icon={solid("edit")}
              style={{ cursor: "pointer", height: "1rem", padding: "6px 0px" }}
              onClick={() => openEditSubgroupModal(record)}
            />
          </Tooltip>
          <Tooltip title="Ștergere">
            <FontAwesomeIcon
              icon={solid("trash")}
              style={{
                marginLeft: "20px",
                cursor: "pointer",
                height: "1rem",
                padding: "6px 0px",
              }}
              onClick={() => handleDelete(record.id)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div
      className={styles.container}
      style={{ display: "flex", flexDirection: "column", marginTop: "0%" }}
    >
      <Layout>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {/* <Button
            onClick={() => navigate.goBack()}
            icon={<FontAwesomeIcon icon={solid("arrow-left")} />}
            type="primary"
          >
            Înapoi
          </Button> */}
          <Typography.Title level={4} style={{ margin: 0 }}>
              {group?.name ?? "—"}
            </Typography.Title>
            </div>
          
            
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openAddSubgroupModal}
            >
              Adaugă subgrup
            </Button>
          
        </div>

        <Table
          className={styles.usersTable}
          dataSource={page?.data || []}
          scroll={{ y: "calc(100vh - 330px)" }}
          sticky
          pagination={false}
          locale={{
            emptyText: "Nu există subgrupuri de discipline adăugate.",
          }}
          columns={columns}
          rowKey={(record: any) => record.id!}
          loading={isLoading}
        />

        <Modal
          open={deleteDlg.open}
          onOk={confirmDelete}
          onCancel={() => setDeleteDlg({ open: false })}
          title="Ștergere"
          width={"400px"}
          okText={t("account.confirm")}
          cancelText={t("account.cancel")}
          cancelButtonProps={{
            style: {
              background: theme.green,
              border: theme.green,
              color: theme.white,
              borderRadius: "10px",
            },
          }}
          okButtonProps={{
            style: {
              background: theme.secondColor,
              border: theme.secondColor,
              borderRadius: "10px",
            },
          }}
        >
          <Typography>Sigur doriți ștergerea subgrupului?</Typography>
        </Modal>

        <SubjectMultiSelectModal
          key={(editingSubgroup?.id || "add") + (subjectsModal.open ? "-open" : "")}
          open={subjectsModal.open}
          onCancel={() => {
            setSubjectsModal((prev) => ({ ...prev, open: false }));
            setEditingSubgroup(null);
          }}
          onSubmit={handleSubmitSubgroup}
          title={subjectsModal.title}
          options={subjectsModal.options}
          loading={subjectsModal.loading}
          initialSelectedIds={subjectsModal.initialSelectedIds}
          showNameInput
          initialName={editingSubgroup?.name ?? ""}
        />
      </Layout>
    </div>
  );
};

export default DisciplineSubgroups;
